/* External dependencies */
import { Turnstile } from "@marsidev/react-turnstile";
import PropTypes from "prop-types";
import React, { useState, useCallback, useMemo } from "react";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import isEmpty from "lodash/isEmpty";
import { Redirect } from "react-router";
/* Internal dependencies */
import { emailConstraints } from "./CommonConstraints";
import logo from "../../images/my-yoast-academy-logo.svg";
import ValidationInputField from "../ValidationInputField";
import ErrorDisplay, { ErrorPropTypeShape } from "../../errors/ErrorDisplay";
import { useTurnstileSubmit } from "./useTurnstileSubmit";
import getEnv from "shared-frontend/functions/getEnv";
import { Title, Button, Link } from "@yoast/ui-library";
import { NavLink } from "react-router-dom";
import * as styles from "./ResetPasswordEmailPageStyles.module.scss";

const TURNSTILE_SITE_KEY = getEnv( "TURNSTILE_SITE_KEY", "1x00000000000000000000AA" );

const messages = defineMessages( {
	passwordResetTitle: {
		id: "reset.password",
		defaultMessage: "Password reset",
	},
	labelEmail: {
		id: "email.address",
		defaultMessage: "Email address",
	},
	emailAddressMessage: {
		id: "email.address.message",
		defaultMessage: "Please enter your email address. You will receive a link to create a new password via email.",
	},
	sendButton: {
		id: "reset.button",
		defaultMessage: "Send password reset email",
	},
	loadingButton: {
		id: "reset.button.loading",
		defaultMessage: "Sending password reset email...",
	},
	backButton: {
		id: "back.button",
		defaultMessage: "Back to login form",
	},
} );

/**
 * Page to send an email to reset the password of an account.
 *
 * @param {object} props The component props.
 *
 * @returns {ReactElement} The rendered component.
 */
const ResetPasswordEmailPage = ( {
	intl,
	email: initialEmail,
	passwordRequestSent,
	loading,
	error,
	attemptResetPasswordEmail,
} ) => {
	const [ email, setEmail ] = useState( initialEmail );
	const [ errors, setErrors ] = useState( [] );
	const [ validating, setValidating ] = useState( false );

	const onResetSubmit = useCallback( ( payload, turnstileToken ) => {
		attemptResetPasswordEmail( { ...payload, turnstileToken } );
	}, [ attemptResetPasswordEmail ] );

	const {
		turnstileRef,
		turnstileError,
		turnstilePending,
		onTurnstileVerify,
		onTurnstileExpire,
		onTurnstileError,
		onTurnstileTimeout,
		onTurnstileScriptError,
		requestSubmit,
	} = useTurnstileSubmit( { onSubmit: onResetSubmit, error } );

	// Memoize the email constraints to avoid recreating on every render.
	const emailConstraintsMemo = useMemo( () => emailConstraints( intl ), [ intl ] );

	/**
	 * Handles email field change from ValidationInputField.
	 *
	 * @param {string} value The input field value.
	 * @param {Array} fieldErrors The email related errors.
	 * @param {boolean} isValidating Whether the email is being validated.
	 */
	const onUpdateEmail = useCallback( ( value, fieldErrors = [], isValidating = false ) => {
		setEmail( value );
		setErrors( fieldErrors );
		setValidating( isValidating );
	}, [] );

	/**
	 * Checks whether the form can be submitted.
	 *
	 * @returns {boolean} Whether the form can be submitted.
	 */
	const canSubmit = useCallback( () => {
		return ! isEmpty( email ) &&
			errors.length === 0 &&
			! loading &&
			! validating &&
			! turnstileError &&
			! turnstilePending;
	}, [ email, errors, loading, validating, turnstileError, turnstilePending ] );

	/**
	 * Handles the form submission.
	 *
	 * @param {Object} event The form submit event.
	 */
	const handleSubmit = useCallback( ( event ) => {
		event.preventDefault();
		if ( ! canSubmit() ) {
			return;
		}
		requestSubmit( { email } );
	}, [ canSubmit, requestSubmit, email ] );

	if ( passwordRequestSent ) {
		return ( <Redirect to={ "/forgot-password/check-your-email" } /> );
	}

	const buttonText = ( loading || turnstilePending ) ? messages.loadingButton : messages.sendButton;
	const isDisabled = ! canSubmit();

	return (
		<div className={ styles.firstColumn }>
			<img src={ logo } alt="MyYoast - Yoast Academy" />
			<Title size="2">
				<FormattedMessage { ...messages.passwordResetTitle } />
			</Title>
			<p>
				<FormattedMessage { ...messages.emailAddressMessage } />
			</p>
			<form onSubmit={ handleSubmit }>
				<ErrorDisplay error={ error } />
				<ValidationInputField
					id="email"
					name="email"
					type="email"
					label={ intl.formatMessage( messages.labelEmail ) }
					errors={ [] }
					onChange={ onUpdateEmail }
					constraint={ emailConstraintsMemo }
					value={ email }
				/>

				{
					TURNSTILE_SITE_KEY &&
					<div>
						<Turnstile
							ref={ turnstileRef }
							siteKey={ TURNSTILE_SITE_KEY }
							options={ { action: "reset-password", theme: "light", size: "flexible" } }
							scriptOptions={ { onError: onTurnstileScriptError } }
							onSuccess={ onTurnstileVerify }
							onExpire={ onTurnstileExpire }
							onError={ onTurnstileError }
							onTimeout={ onTurnstileTimeout }
						/>
					</div>
				}

				<div className={ styles.actions }>
					<Button
						type="submit"
						disabled={ isDisabled }
						aria-disabled={ isDisabled }
					>
						<FormattedMessage { ...buttonText } />
					</Button>
					<Link as={ NavLink } to="../login">
						<FormattedMessage { ...messages.backButton } />
					</Link>
				</div>
			</form>
		</div>
	);
};

ResetPasswordEmailPage.propTypes = {
	intl: intlShape.isRequired,
	email: PropTypes.string,
	passwordRequestSent: PropTypes.bool,
	loading: PropTypes.bool,
	error: ErrorPropTypeShape,
	attemptResetPasswordEmail: PropTypes.func.isRequired,
};

ResetPasswordEmailPage.defaultProps = {
	email: "",
	passwordRequestSent: false,
	loading: false,
	error: null,
};

export default injectIntl( ResetPasswordEmailPage );
